// Языки листингов: подсветка в редакторе (в DOCX подсветки нет - оформление по 6.1), определение языка по
// тексту и по расширению файла, нормализация кода (6.1.1–6.1.2): табуляции -> 4 пробела, типографские
// кавычки и тире в синтаксисе -> ASCII.
import type { Extension } from '@codemirror/state';
import { StreamLanguage } from '@codemirror/language';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { javascript } from '@codemirror/lang-javascript';
import { java } from '@codemirror/lang-java';
import { sql } from '@codemirror/lang-sql';
import { octave } from '@codemirror/legacy-modes/mode/octave';
import { pascal } from '@codemirror/legacy-modes/mode/pascal';
import { shell } from '@codemirror/legacy-modes/mode/shell';
import { c as cMode, csharp, kotlin } from '@codemirror/legacy-modes/mode/clike';
import { powerShell } from '@codemirror/legacy-modes/mode/powershell';
import { xml } from '@codemirror/legacy-modes/mode/xml';
import { css } from '@codemirror/legacy-modes/mode/css';
import { yaml } from '@codemirror/legacy-modes/mode/yaml';
import { r as rMode } from '@codemirror/legacy-modes/mode/r';
import { lua } from '@codemirror/legacy-modes/mode/lua';
import { vb } from '@codemirror/legacy-modes/mode/vb';
import { verilog } from '@codemirror/legacy-modes/mode/verilog';
import { vhdl } from '@codemirror/legacy-modes/mode/vhdl';
import { gas } from '@codemirror/legacy-modes/mode/gas';
import { go } from '@codemirror/legacy-modes/mode/go';
import { rust } from '@codemirror/legacy-modes/mode/rust';
import { stex } from '@codemirror/legacy-modes/mode/stex';
import { json } from '@codemirror/legacy-modes/mode/javascript';

export interface Lang { id: string; title: string; ext: string[]; mode: () => Extension | Extension[] }
const L = (p: any) => () => StreamLanguage.define(p);

export const LANGS: Lang[] = [
  { id: '', title: 'Текст', ext: ['txt', 'log', 'out'], mode: () => [] },
  { id: 'python', title: 'Python', ext: ['py', 'pyw', 'ipynb'], mode: () => python() },
  { id: 'c', title: 'C', ext: ['c', 'h'], mode: L(cMode) },
  { id: 'cpp', title: 'C++ / Arduino', ext: ['cpp', 'cc', 'cxx', 'hpp', 'hh', 'ino'], mode: () => cpp() },
  { id: 'csharp', title: 'C#', ext: ['cs'], mode: L(csharp) },
  { id: 'java', title: 'Java', ext: ['java'], mode: () => java() },
  { id: 'kotlin', title: 'Kotlin', ext: ['kt', 'kts'], mode: L(kotlin) },
  { id: 'javascript', title: 'JavaScript', ext: ['js', 'mjs', 'cjs', 'jsx'], mode: () => javascript({ jsx: true }) },
  { id: 'typescript', title: 'TypeScript', ext: ['ts', 'tsx'], mode: () => javascript({ typescript: true, jsx: true }) },
  { id: 'json', title: 'JSON', ext: ['json'], mode: L(json) },
  { id: 'sql', title: 'SQL', ext: ['sql'], mode: () => sql() },
  { id: 'matlab', title: 'MATLAB / Octave', ext: ['m'], mode: L(octave) },
  { id: 'pascal', title: 'Pascal / Delphi / ST (ПЛК)', ext: ['pas', 'dpr', 'pp', 'st', 'scl'], mode: L(pascal) },
  { id: 'bash', title: 'Bash / Shell', ext: ['sh', 'bash'], mode: L(shell) },
  { id: 'bat', title: 'Командный файл (.bat)', ext: ['bat', 'cmd'], mode: () => [] },
  { id: 'powershell', title: 'PowerShell', ext: ['ps1', 'psm1'], mode: L(powerShell) },
  { id: 'go', title: 'Go', ext: ['go'], mode: L(go) },
  { id: 'rust', title: 'Rust', ext: ['rs'], mode: L(rust) },
  { id: 'r', title: 'R', ext: ['r'], mode: L(rMode) },
  { id: 'lua', title: 'Lua', ext: ['lua'], mode: L(lua) },
  { id: 'vb', title: 'Visual Basic / VBA', ext: ['vb', 'bas', 'vba'], mode: L(vb) },
  { id: 'verilog', title: 'Verilog', ext: ['v', 'sv'], mode: L(verilog) },
  { id: 'vhdl', title: 'VHDL', ext: ['vhd', 'vhdl'], mode: L(vhdl) },
  { id: 'asm', title: 'Ассемблер', ext: ['asm', 's'], mode: L(gas) },
  { id: 'html', title: 'HTML / XML', ext: ['html', 'htm', 'xml', 'xaml', 'svg'], mode: L(xml) },
  { id: 'css', title: 'CSS', ext: ['css'], mode: L(css) },
  { id: 'yaml', title: 'YAML', ext: ['yaml', 'yml'], mode: L(yaml) },
  { id: 'latex', title: 'LaTeX', ext: ['tex'], mode: L(stex) },
];

export function langById(id: string): Lang {
  return LANGS.find((l) => l.id === id) || LANGS.find((l) => l.id === id.toLowerCase()) || ALIASES[id.toLowerCase()] || LANGS[0];
}
const ALIASES: Record<string, Lang> = {};
for (const [a, id] of [['py', 'python'], ['c++', 'cpp'], ['cs', 'csharp'], ['js', 'javascript'], ['ts', 'typescript'], ['sh', 'bash'],
  ['shell', 'bash'], ['octave', 'matlab'], ['delphi', 'pascal'], ['cmd', 'bat'], ['batch', 'bat'], ['ps1', 'powershell'], ['xml', 'html'],
  ['tex', 'latex'], ['arduino', 'cpp'], ['plaintext', ''], ['text', ''], ['txt', ''], ['objective-c', 'c'], ['st', 'pascal'],
  ['javascriptreact', 'javascript'], ['typescriptreact', 'typescript'], ['yml', 'yaml'], ['vbnet', 'vb']] as const) {
  ALIASES[a] = LANGS.find((l) => l.id === id)!;
}

/** Язык по имени файла ('main.py' -> 'python'), '' - неизвестно. */
export function langFromFilename(name: string): string | null {
  const ext = (name.match(/\.([^.]+)$/) || [])[1]?.toLowerCase();
  if (!ext) return null;
  const l = LANGS.find((x) => x.ext.includes(ext));
  return l ? l.id : null;
}

export const CODE_EXT = new Set(LANGS.flatMap((l) => l.ext).filter((e) => !['txt', 'log', 'out', 'svg', 'ipynb'].includes(e)));

/** Определить язык по тексту: {id, score}; score < 2 - не уверены. */
export function detectLang(code: string): { id: string; score: number } {
  const s = code.slice(0, 6000);
  const has = (re: RegExp) => (re.test(s) ? 1 : 0);
  const sc: Record<string, number> = {
    python: has(/^\s*(def|class)\s+\w+.*:\s*$/m) * 2 + has(/^\s*(import \w+|from \S+ import )/m) * 2 + has(/\bprint\(/) + has(/\bself\./) + has(/^\s*(elif|except|with)\b.*:\s*$/m) + has(/__name__|__init__/) + has(/^\s*#(?!include|define|!)/m) * 0.5 - has(/;\s*$/m) - has(/[{}]\s*$/m),
    cpp: has(/#include\s*<(iostream|vector|string|map|algorithm)>/) * 3 + has(/std::|cout\s*<<|cin\s*>>/) * 2 + has(/\bnamespace\b|\btemplate\s*</) + has(/void (setup|loop)\s*\(\)/) * 3,
    c: has(/#include\s*[<"][\w/]+\.h[>"]/) * 2 + has(/\bprintf\s*\(|\bscanf\s*\(|\bmalloc\s*\(/) * 2 + has(/\bint\s+main\s*\(/),
    csharp: has(/using System(\.\w+)*;/) * 3 + has(/Console\.Write/) * 2 + has(/\bnamespace\s+\w+/),
    java: has(/public\s+(static\s+)?(class|void|final)/) * 2 + has(/System\.out\.print/) * 3 + has(/import java\./) * 3,
    javascript: has(/\bfunction\s*\w*\s*\(/) + has(/\b(const|let)\s+\w+\s*=/) + has(/=>\s*[{(]?/) + has(/console\.log|document\.|require\(|module\.exports/) * 2,
    typescript: has(/\binterface\s+\w+\s*\{|:\s*(string|number|boolean)\b|\bexport\s+type\b/) * 2,
    sql: has(/^\s*(SELECT|INSERT\s+INTO|UPDATE|DELETE\s+FROM|CREATE\s+(TABLE|VIEW|INDEX)|ALTER\s+TABLE|DROP\s+TABLE)\b/im) * 3 + has(/\bFROM\s+\w+|\bWHERE\b/i),
    matlab: has(/^\s*%/m) + has(/^\s*end\s*;?\s*$/m) + has(/\b(disp|plot|zeros|ones|linspace|figure|subplot|xlabel|ylabel|tf|step|bode|feedback|clc|clear all)\s*[(;]?/) * 2 + has(/\.\*|\.\^|\.\//) + has(/^\s*function\s+.*=\s*\w+\(/m) * 2,
    pascal: has(/^\s*(program|unit)\s+\w+;/im) * 3 + has(/^\s*begin\s*$/im) + has(/^\s*end\.?\s*;?\s*$/im) * 0.5 + has(/\b(writeln|readln)\b/i) * 2 + has(/:=/) + has(/^\s*(var|VAR_INPUT|VAR_OUTPUT|END_VAR|FUNCTION_BLOCK)\b/m) * 2,
    bash: has(/^#!.*\b(ba|z)?sh\b/) * 3 + has(/^\s*(echo|sudo|apt|export|cd|ls|grep|chmod)\s/m) + has(/\$\{?\w+\}?/) + has(/^\s*(fi|done|esac)\s*$/m) * 2,
    bat: has(/^\s*@?echo\s+off/im) * 3 + has(/^\s*rem\s/im) * 2 + has(/%\w+%|%~?\d/) + has(/^\s*(goto|set\s+\/a|if\s+exist)\b/im) + has(/^\s*(ping|tracert|ipconfig|netstat|nslookup|arp|route|pathping)\b/im) * 2,
    powershell: has(/\b(Get|Set|New|Remove|Write|Invoke|Test)-[A-Z]\w+/) * 3 + has(/\$\w+\s*=/) + has(/\s-(eq|ne|lt|gt|like)\s/),
    json: /^\s*[{[]/.test(s) && /[}\]]\s*$/.test(s) && (() => { try { JSON.parse(s); return true; } catch { return false; } })() ? 5 : 0,
    html: has(/^\s*<(!DOCTYPE|html|\?xml|div|body|head|svg|root)\b/i) * 3,
    verilog: has(/\bmodule\s+\w+/) + has(/\bendmodule\b/) * 3 + has(/always\s*@/) * 2,
    vhdl: has(/\bentity\s+\w+\s+is\b/i) * 3 + has(/\barchitecture\b/i) + has(/std_logic/i) * 2,
    asm: has(/^\s*(mov|push|pop|jmp|call|ret|int|add|sub|cmp|jne|je)\s+/im) * 2 + has(/\b(eax|ebx|rax|rsp|al|ah)\b/i),
    latex: has(/\\(documentclass|begin\{|section\{|usepackage)/) * 3,
    yaml: has(/^[\w-]+:\s*(\S.*)?$/m) * 0.5 + has(/^\s*-\s+\w+:/m),
    go: has(/^package\s+\w+/m) * 2 + has(/\bfunc\s+\w+\(/) * 2 + has(/fmt\.Print/) * 2,
    rust: has(/\bfn\s+\w+\(/) * 2 + has(/\blet\s+mut\b/) * 2 + has(/println!\(/) * 2,
  };
  if (sc.cpp > 0 && sc.c > 0) sc.cpp += 1;
  if (sc.typescript > 0) sc.typescript += sc.javascript;
  let best = ''; let score = 0;
  for (const [k, v] of Object.entries(sc)) if (v > score) { best = k; score = v; }
  return { id: score >= 2 ? best : '', score };
}

/** Нормализация вставленного кода: переводы строк, табуляции -> 4 пробела (6.1.2), неразрывные и
 *  «нулевые» пробелы, общий левый отступ (если весь фрагмент вставлен с отступом). */
export function normalizeCode(text: string): string {
  let s = text.replace(/\r\n?/g, '\n').replace(/[​‌‍﻿]/g, '').replace(/ /g, ' ');
  s = s.split('\n').map((ln) => expandTabs(ln).replace(/\s+$/, '')).join('\n');
  // убрать пустые строки по краям
  s = s.replace(/^\n+/, '').replace(/\n+$/, '');
  // общий отступ (фрагмент метода, скопированный из середины файла)
  const ind = s.split('\n').filter((l) => l.trim()).map((l) => l.length - l.trimStart().length);
  const min = ind.length ? Math.min(...ind) : 0;
  if (min > 0) s = s.split('\n').map((l) => l.slice(Math.min(min, l.length - l.trimStart().length))).join('\n');
  return s;
}

export function expandTabs(line: string, tab = 4): string {
  if (!line.includes('\t')) return line;
  let out = '';
  for (const ch of line) {
    if (ch === '\t') out += ' '.repeat(tab - (out.length % tab));
    else out += ch;
  }
  return out;
}

// --- символы синтаксиса (6.1.1): как code_syntax_text в lib/sto_check.py - строки и комментарии не трогаем
const SYN_BAD = /[“”„«»‘’–—…−×]/;

/** Диапазоны строковых литералов и комментариев (там допустимы любые символы). */
function protectedRanges(code: string): [number, number][] {
  const out: [number, number][] = [];
  const re = /\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\\n])*"|(?<![\w)\]'])'(?:\\.|[^'\\\n])*'|\/\/[^\n]*|(?:^|(?<=\s))(?:#|%|--(?=\s)|;;|REM\b|rem\b)[^\n]*/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(code))) { out.push([m.index, m.index + m[0].length]); if (!m[0].length) re.lastIndex++; }
  return out;
}

/** Есть ли типографские символы в синтаксисе кода: {quotes, other: ['–', '…']}. */
export function syntaxIssues(code: string): { quotes: boolean; other: string[] } {
  const pr = protectedRanges(code);
  const inP = (i: number) => pr.some(([a, b]) => i >= a && i < b);
  let quotes = false; const other = new Set<string>();
  for (let i = 0; i < code.length; i++) {
    const ch = code[i];
    if (!SYN_BAD.test(ch) || inP(i)) continue;
    if (/[“”„«»‘’]/.test(ch)) quotes = true; else other.add(ch);
  }
  return { quotes, other: [...other] };
}

/** Исправить: кавычки “…” ‘…’ -> "…" '…' (в т. ч. кавычки-ограничители строк из Word), тире -> -, … -> ...,
 *  × -> *, − -> -. Внутри «прямых» строк и комментариев ничего не меняется. */
export function fixSyntaxChars(code: string): string {
  let s = code;
  // код целиком из Word: прямых кавычек нет, все - типографские -> заменить все
  if (!/"/.test(s) && /[“”„]/.test(s)) s = s.replace(/[“”„]/g, '"');
  if (!/'/.test(s) && /[‘’]/.test(s)) s = s.replace(/[‘’]/g, "'");
  const pr = protectedRanges(s);
  const inP = (i: number) => pr.some(([a, b]) => i >= a && i < b);
  const map: Record<string, string> = { '“': '"', '”': '"', '„': '"', '«': '"', '»': '"', '‘': "'", '’': "'", '–': '-', '—': '-', '−': '-', '…': '...', '×': '*' };
  let out = '';
  for (let i = 0; i < s.length; i++) out += !inP(i) && map[s[i]] ? map[s[i]] : s[i];
  return out;
}

/** Строк с отступом, не кратным 2 (6.1.2). */
export function oddIndentLines(code: string): number {
  return code.split('\n').filter((l) => l.trim() && (l.length - l.replace(/^ +/, '').length) % 2).length;
}

/** Регулярное выражение «строка похожа на код» (как CODE-AS-TEXT в чекере). */
export const CODE_LINE_RE = /^\s*(?:#include|import\s+\w|from\s+\S+\s+import|def\s+\w+\(|class\s+\w+[:(\s{]|public\s|private\s|int\s+\w+\s*[(=;]|void\s+\w+\(|for\s*\(|while\s*\(|if\s*\(.*\)\s*\{|return\s.*;|\}\s*$|\{\s*$|<\?php|using\s+namespace|printf\(|console\.\w+\(|System\.out)/;
